import React, { useState } from 'react';
import { Link2, Mail, Check } from 'lucide-react';

/**
 * "Save / Share" button group shown on the Quote Calculator once the user
 * has selected at least a product + format. Copies the current quote URL
 * to the clipboard and offers a mailto: with a prefilled subject + body.
 *
 * The `shareUrl` comes from the parent (which already builds the
 * draft URL from URLSearchParams). We compose an absolute URL using the
 * current origin so the link works when pasted anywhere.
 */
export const SaveQuoteButton = ({ draftUrl, productName, totalPrice, currency = 'USD' }) => {
  const [copied, setCopied] = useState(false);

  if (!draftUrl) return null;

  const absoluteUrl = `${window.location.origin}${draftUrl}`;
  const subject = encodeURIComponent(`Quote for ${productName || 'custom stickers'} — Evergreen Graphics`);
  const body = encodeURIComponent(
    `Hi,\n\nHere's the quote I just put together on evergreengraphics.com:\n\n` +
      `Product: ${productName || 'custom order'}\n` +
      (totalPrice ? `Estimated total: $${Number(totalPrice).toFixed(2)} ${currency}\n` : '') +
      `\nResume / edit this quote here:\n${absoluteUrl}\n\n` +
      `— Sent from Evergreen Graphics`
  );

  const copy = async () => {
    try {
      // eslint-disable-next-line no-undef
      await navigator.clipboard.writeText(absoluteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard permission denied / unsupported — fall back to selection prompt
      // eslint-disable-next-line no-alert
      window.prompt('Copy this quote URL:', absoluteUrl);
    }
  };

  return (
    <div
      className="mt-4 flex flex-wrap items-center gap-2 px-4 py-2.5 rounded-lg bg-green-50/80 border border-[#4A7C59]/20"
      data-testid="save-quote-bar"
    >
      <p className="text-xs text-[#2F5D3A] font-medium mr-1">
        Save this quote →
      </p>
      <button
        type="button"
        onClick={copy}
        data-testid="save-quote-copy"
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
          copied
            ? 'bg-green-600 text-white'
            : 'bg-white border border-[#4A7C59] text-[#4A7C59] hover:bg-[#4A7C59] hover:text-white'
        }`}
      >
        {copied ? <Check className="w-3.5 h-3.5" /> : <Link2 className="w-3.5 h-3.5" />}
        {copied ? 'Link copied!' : 'Copy link'}
      </button>
      <a
        href={`mailto:?subject=${subject}&body=${body}`}
        data-testid="save-quote-email"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-white border border-[#4A7C59] text-[#4A7C59] hover:bg-[#4A7C59] hover:text-white transition-all"
      >
        <Mail className="w-3.5 h-3.5" />
        Email me the link
      </a>
    </div>
  );
};
